import { useState, useEffect } from 'react';
import { Menu, X, ArrowRight, ArrowUpRight } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';

const TEAL_LOGO = "https://customer-assets.emergentagent.com/job_citizen-refresh/artifacts/2gf9jka6_customcolor_logo_transparent_background.png";

const navLinks = [
  { label: 'The Firm', href: '#about', homeOnly: true },
  { label: 'Methodology', href: '#methodology', homeOnly: true },
  { label: 'Family Office', href: 'https://familyoffice.globalcitizenconsult.com', isExternal: true, plain: true },
  { label: 'Blog', href: '/blog', isRoute: true },
  { label: 'Manifesto', href: '/manifesto', isRoute: true },
  { label: 'Contact', href: '#contact', homeOnly: true },
  { label: 'Offshore Services', href: 'https://www.gcc-offshore.com', isExternal: true },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const isHome = location.pathname === '/';

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const handleClick = (e, link) => {
    e.preventDefault();
    setMobileOpen(false);

    if (link.isRoute) {
      navigate(link.href);
      return;
    }

    if (!isHome) {
      navigate('/');
      setTimeout(() => {
        const el = document.querySelector(link.href);
        if (el) el.scrollIntoView({ behavior: 'smooth' });
      }, 300);
      return;
    }

    const el = document.querySelector(link.href);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  const goHome = (e) => {
    e.preventDefault();
    setMobileOpen(false);
    if (!isHome) {
      navigate('/');
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  return (
    <nav
      data-testid="navbar"
      className="fixed top-0 left-0 right-0 z-50 transition-all duration-500 bg-white/90 backdrop-blur-xl border-b border-gray-200/60 shadow-sm"
    >
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="flex items-center justify-between h-20">
          <a
            href="/"
            data-testid="navbar-logo"
            className="flex items-center gap-3 group"
            onClick={goHome}
          >
            <img
              src={TEAL_LOGO}
              alt="Global Citizen Consulting"
              className="h-10 md:h-12 w-auto"
            />
          </a>

          <div className="hidden lg:flex items-center gap-6">
            {navLinks.map((link) =>
              link.isExternal ? (
                <a
                  key={link.href}
                  href={link.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`nav-link-${link.label.toLowerCase().replace(/\s/g, '-')}`}
                  className={`flex items-center gap-1 text-sm font-semibold transition-colors duration-300 tracking-wide uppercase text-[#1a2332] ${link.plain ? 'hover:text-[#1a7a7a]' : 'hover:text-[#b8912e]'}`}
                  style={{ fontFamily: "'Montserrat', sans-serif" }}
                >
                  {link.label}
                  {!link.plain && <ArrowUpRight size={13} className="text-[#d4af37]" strokeWidth={2.5} />}
                </a>
              ) : (
              <a
                key={link.href}
                href={link.href}
                data-testid={`nav-link-${link.label.toLowerCase().replace(/\s/g, '-')}`}
                className={`text-sm font-semibold transition-colors duration-300 tracking-wide uppercase ${
                  link.isRoute && location.pathname === link.href
                    ? 'text-[#1a7a7a]'
                    : 'text-[#1a2332] hover:text-[#1a7a7a]'
                }`}
                style={{ fontFamily: "'Montserrat', sans-serif" }}
                onClick={(e) => handleClick(e, link)}
              >
                {link.label}
              </a>
              )
            )}
            <a
              href="https://calendly.com/chr-globalcitizen/video-consultation-clone"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="nav-consultation-button"
              className="flex items-center gap-2 bg-[#1a7a7a] text-white font-bold px-5 py-2.5 rounded-full text-sm hover:bg-[#1a6868] transition-all duration-300"
              style={{ fontFamily: "'Montserrat', sans-serif" }}
            >
              Request an Introduction
              <ArrowRight size={14} />
            </a>
          </div>

          <button
            data-testid="mobile-menu-toggle"
            className={`lg:hidden p-2 text-[#1a1a1a]`}
            onClick={() => setMobileOpen(!mobileOpen)}
          >
            {mobileOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {mobileOpen && (
          <div
            data-testid="mobile-menu"
            className={`lg:hidden py-6 border-t animate-fade-in border-gray-200`}
          >
            <div className="flex flex-col gap-4">
              {navLinks.map((link) =>
                link.isExternal ? (
                  <a
                    key={link.href}
                    href={link.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`mobile-nav-link-${link.label.toLowerCase().replace(/\s/g, '-')}`}
                    className={`flex items-center gap-1 transition-colors py-2 text-sm uppercase tracking-wide font-semibold text-[#1a2332] ${link.plain ? 'hover:text-[#1a7a7a]' : 'hover:text-[#b8912e]'}`}
                    style={{ fontFamily: "'Montserrat', sans-serif" }}
                    onClick={() => setMobileOpen(false)}
                  >
                    {link.label}
                    {!link.plain && <ArrowUpRight size={13} className="text-[#d4af37]" strokeWidth={2.5} />}
                  </a>
                ) : (
                <a
                  key={link.href}
                  href={link.href}
                  className="transition-colors py-2 text-sm uppercase tracking-wide font-semibold text-[#1a2332] hover:text-[#1a7a7a]"
                  style={{ fontFamily: "'Montserrat', sans-serif" }}
                  onClick={(e) => handleClick(e, link)}
                >
                  {link.label}
                </a>
                )
              )}
              <a
                href="https://calendly.com/chr-globalcitizen/video-consultation-clone"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 bg-[#1a7a7a] text-white font-bold px-6 py-3 rounded-full text-sm hover:bg-[#1a6868] transition-all mt-2"
                style={{ fontFamily: "'Montserrat', sans-serif" }}
                onClick={() => setMobileOpen(false)}
              >
                <ArrowRight size={14} />
                Request an Introduction
              </a>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}
